/**
 * Where Mux fetches a native video upload from (BAL-721, private media library).
 *
 * A shown video is copied to R2, the public ingest origin (Rule 11, decision
 * D1). A private video never gets a public copy: Mux fetches its original
 * through a short-lived signed URL on the private `dancer-media` bucket, and
 * Mux playback is signed either way.
 *
 * WIP: docs/wip/active/WIP_building_private_media_library_2026_10_24.md
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result, ServiceError } from '@kit/shared/result';
import { StorageBuckets, type R2Service } from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';

type MediaRow = Database['public']['Tables']['artist_media']['Row'];

export interface VideoIngestSource {
  url: string;
  /** The public R2 key written for the source, or null when none was. */
  r2Key: string | null;
}

/**
 * Map an uploaded video's MIME type to the extension of its R2 key. Keeps the
 * key URL-safe (no user-controlled file_name); Mux ingests any of these.
 */
const VIDEO_MIME_EXT: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};
const DEFAULT_VIDEO_EXT = 'mp4';

/** How long Mux may take to fetch a private original (Rule 11 caps signed
 * dancer-video URLs at 24 h). */
export const PRIVATE_INGEST_URL_TTL_SECONDS = 6 * 60 * 60;

/** Shown video: copy the raw bytes to R2 and return the public R2 URL. */
export async function publishVideoSourceToR2(
  client: SupabaseClient<Database>,
  r2: R2Service,
  media: MediaRow,
  userId: string,
): Promise<Result<VideoIngestSource>> {
  const download = await client.storage
    .from(StorageBuckets.DANCER_MEDIA)
    .download(media.storage_path ?? '');
  if (download.error || !download.data) {
    return Result.fail(
      ServiceError.internal('storage download failed', download.error),
    );
  }
  const bytes = Buffer.from(await download.data.arrayBuffer());

  // A deterministic key from media.id and a mime-based extension, never the
  // raw file_name: getPublicUrl does no escaping, so a space, '#', '?' or a
  // non-ASCII character would malform the URL handed to Mux.
  const ext = VIDEO_MIME_EXT[media.mime_type ?? ''] ?? DEFAULT_VIDEO_EXT;
  const r2Key = `dancer-videos/${userId}/${media.id}/source.${ext}`;
  const upload = await r2.upload(r2Key, bytes, {
    contentType: media.mime_type ?? 'video/mp4',
  });
  if (upload.isFailure) {
    return Result.fail(
      ServiceError.internal('r2 upload failed', upload.getError()),
    );
  }
  return Result.ok({ url: r2.getPublicUrl(r2Key), r2Key });
}

/** Private video: a short-lived signed URL on the private bucket, no copy. */
export async function signPrivateVideoSource(
  client: SupabaseClient<Database>,
  media: MediaRow,
): Promise<Result<VideoIngestSource>> {
  const { data, error } = await client.storage
    .from(StorageBuckets.DANCER_MEDIA)
    .createSignedUrl(media.storage_path ?? '', PRIVATE_INGEST_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) {
    return Result.fail(
      ServiceError.internal('private source signing failed', error),
    );
  }
  return Result.ok({ url: data.signedUrl, r2Key: null });
}
